import { Component, Input, Output, EventEmitter, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { QuotaUsageAnalyticsDto } from '../../../services/dashboard.interface';

@Component({
  selector: 'app-quota-animals-grid',
  imports: [CommonModule, MatCardModule, TranslateModule],
  templateUrl: './quota-animals-grid.html',
  styleUrl: './quota-animals-grid.css',
})
export class QuotaAnimalsGridComponent {
  private translate = inject(TranslateService);

  @Input() cards: QuotaUsageAnalyticsDto[] = [];
  @Input() active: QuotaUsageAnalyticsDto | null = null;
  @Output() selectCard = new EventEmitter<QuotaUsageAnalyticsDto | null>();

  getMeatName(card: QuotaUsageAnalyticsDto): string {
    const lang = this.translate.currentLang || this.translate.defaultLang;
    return lang === 'ky' ? (card.meatType?.nameKy || card.meatType?.nameRu || '—') : (card.meatType?.nameRu || '—');
  }

  onCardClick(card: QuotaUsageAnalyticsDto): void {
    this.selectCard.emit(card);
  }

  isSelected(card: QuotaUsageAnalyticsDto): boolean {
    return this.active?.quotaId === card.quotaId &&
           this.active?.meatType?.alias === card.meatType?.alias;
  }

  getProgress(card: QuotaUsageAnalyticsDto): number {
    const m2m = card.quotaComparePreviousMonth?.percentage;
    if (typeof m2m === 'number' && !isNaN(m2m)) {
      return this.clamp(m2m);
    }

    const u = card.usagePercentage;
    if (typeof u === 'number' && !isNaN(u)) {
      return this.clamp(u);
    }

    if (card.deliveredVolume != null && card.totalVolume) {
      return this.clamp((card.deliveredVolume / card.totalVolume) * 100);
    }
    return 0;
  }

  private clamp(n: number, min = 0, max = 100): number {
    return Math.min(max, Math.max(min, n));
  }

  getCircleDashOffset(progress: number, radius: number): number {
    const circumference = 2 * Math.PI * radius;
    const clampedProgress = Math.max(0, Math.min(100, progress));
    return circumference * (1 - clampedProgress / 100);
  }

  kgToTons(kg: number | undefined | null): string {
    if (kg == null) return '0';
    const tons = kg / 1000;
    return tons.toLocaleString('ru-RU', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 3
    });
  }

  getMeatIcon(alias: string | undefined): string {
    switch (alias) {
      case 'BEEF':
        return 'assets/icons/cow.svg';
      case 'PORK':
        return 'assets/icons/pork.svg';
      case 'CHICKEN':
        return 'assets/icons/chicken.svg';
      case 'TOTAL':
        return 'assets/icons/animals.svg';
      default:
        return 'assets/icons/animals.svg';
    }
  }

  getGrowthData(card: QuotaUsageAnalyticsDto): {
    type: 'increase' | 'decrease' | 'equal' | null;
    percentage: string;
    color: string;
    arrow: string;
    fullText: string;
  } | null {
    const cmp = card.quotaComparePreviousMonth;
    if (!cmp) return null;

    const kind = (cmp.compare ?? '').trim().toUpperCase();
    const pct = typeof cmp.percentage === 'number'
      ? `${cmp.percentage.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}%`
      : '—';

    if (kind === 'EQUAL') {
      return {
        type: 'equal',
        percentage: pct,
        color: 'var(--text-secondary)',
        arrow: '↔',
        fullText: this.translate.instant('dashboard.noChange')
      };
    }

    const isUp = kind === 'INCREASE';
    const growthText = isUp ? this.translate.instant('dashboard.growth') : this.translate.instant('dashboard.decline');
    const comparedText = this.translate.instant('dashboard.comparedToLastMonth');
    return {
      type: isUp ? 'increase' : 'decrease',
      percentage: pct,
      color: isUp ? '#1D5B37' : '#C0352B',
      arrow: isUp ? '📈' : '📉',
      fullText: `${growthText}: ${pct} ${comparedText}`
    };
  }
}
